import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '@chainlit/react-client';

import LeftSidebar from '@/components/LeftSidebar';
import BillingDashboard from '../components/dashboard/BillingDashboard';
import DokumentDashboard from '../components/dashboard/DokumentDashboard';
import EurlexDashboard from '../components/dashboard/EurlexDashboard';
import ProfileDashboard from '../components/dashboard/ProfileDashboard';
import SearchDashboard from '../components/dashboard/SearchDashboard';
import UpgradeDashboard from '../components/dashboard/UpgradeDashboard';
import UsersDashboard from '../components/dashboard/UsersDashboard';
import MallarPaymentsAdmin from '../components/dashboard/MallarPaymentsAdmin';
// Temporarily disabled admin cards
// import DocumentUploadAdmin from '../components/dashboard/DocumentUploadAdmin';
// import GdriveSyncAdmin from '../components/dashboard/GdriveSyncAdmin';
// import NjaSyncAdmin from '../components/dashboard/NjaSyncAdmin';
// import EmbeddingsAdmin from '../components/dashboard/EmbeddingsAdmin';
// import MailchimpSyncAdmin from '../components/dashboard/MailchimpSyncAdmin';
import SidebarTrigger from '@/components/header/SidebarTrigger';
import { useTranslation } from '@/components/i18n/Translator';
import {
  SidebarInset,
  SidebarProvider,
  useSidebar
} from '@/components/ui/sidebar';

const DashboardContent = ({
  isAdmin,
  renderContent,
  userName
}: any) => {
  const { open, openMobile, isMobile } = useSidebar();
  const sidebarOpen = isMobile ? openMobile : open;
  const { t } = useTranslation();

  const mainContent = (
    <div className="flex flex-col h-full min-h-screen w-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {!sidebarOpen && <SidebarTrigger />}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('dashboard.welcome', { userName })}
              </h1>
              <p className="text-gray-600">{t('dashboard.description')}</p>
            </div>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full border border-blue-200">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
              {t('dashboard.administrator')}
            </div>
          )}
        </div>
      </div>

      {/* Page Content */}
      <div className="flex-1 overflow-y-auto p-6 bg-white min-h-0">
        {renderContent()}
      </div>
    </div>
  );

  return (
    <>
      <LeftSidebar />
      <SidebarInset className="max-h-svh">{mainContent}</SidebarInset>
    </>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('profile');

  // Ensure theme variables are applied
  useEffect(() => {
    // Force theme application if window.theme exists
    if (window.theme?.light) {
      const root = document.documentElement;
      Object.entries(window.theme.light).forEach(([key, value]) => {
        root.style.setProperty(key, value);
      });
    }
  }, []);

  // Check if user is admin - try multiple possible locations for role data
  const isAdmin =
    user?.metadata?.role === 'ADMIN' ||
    user?.role === 'ADMIN' ||
    (user?.metadata &&
      JSON.parse(JSON.stringify(user.metadata))?.role === 'ADMIN');

  // Debug user data
  useEffect(() => {
    console.log('Dashboard user data:', user);
    console.log('User metadata:', user?.metadata);
    console.log('User role:', user?.metadata?.role);
    console.log('Is admin:', isAdmin);

    // Also set window.user for debugging
    if (user) {
      window.user = user;
    }
  }, [user, isAdmin]);

  // If user tries to access admin section without permissions, redirect to profile
  useEffect(() => {
    if ((activeSection === 'users' || activeSection === 'mallar-payments') && !isAdmin) {
      setActiveSection('profile');
    }
  }, [activeSection, isAdmin]);

  // Handle navigation state from upgrade banner
  useEffect(() => {
    if (location.state?.activeSection) {
      setActiveSection(location.state.activeSection);
      // Clear the state to avoid staying on upgrade section on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Handle hash fragment navigation (e.g., #upgrade)
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (
      hash &&
      [
        'profile',
        'search',
        'billing',
        'upgrade',
        'users',
        'eurlex',
        'dokument',
        'mallar-payments'
      ].includes(hash)
    ) {
      setActiveSection(hash);
    }
  }, [location.hash]);

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileDashboard />;
      case 'search':
        return <SearchDashboard />;
      case 'eurlex':
        return <EurlexDashboard />;
      case 'dokument':
        return <DokumentDashboard />;
      case 'billing':
        return (
          <BillingDashboard
            onNavigateToUpgrade={() => setActiveSection('upgrade')}
          />
        );
      case 'upgrade':
        return <UpgradeDashboard />;
      case 'users':
        return isAdmin ? <UsersDashboard /> : <ProfileDashboard />;
      case 'mallar-payments':
        return isAdmin ? (
          <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-6 items-start">
            <MallarPaymentsAdmin />
            {/* Temporarily disabled admin cards */}
            {/* <DocumentUploadAdmin /> */}
            {/* <GdriveSyncAdmin /> */}
            {/* <NjaSyncAdmin /> */}
            {/* <EmbeddingsAdmin /> */}
            {/* <MailchimpSyncAdmin /> */}
          </div>
        ) : (
          <ProfileDashboard />
        );
      default:
        return <ProfileDashboard />;
    }
  };

  // Get user display name
  const userName = user?.display_name || user?.identifier || 'User';

  return (
    <SidebarProvider>
      <DashboardContent
        isAdmin={isAdmin}
        renderContent={renderContent}
        userName={userName}
      />
    </SidebarProvider>
  );
};

export default Dashboard;
